import { ConnectButton } from "@rainbow-me/rainbowkit"
import {
	createRootRoute,
	type ErrorComponentProps,
	Link,
	Outlet,
	useRouter,
} from "@tanstack/react-router"
import { AlertCircle, Home, RefreshCw } from "lucide-react"
import { lazy, Suspense, useEffect } from "react"
import { Toaster } from "sonner"
import { useAccount, useConnect } from "wagmi"
import { useExecutionMode } from "../hooks/useExecutionMode"
import { E2E_MODE } from "../lib/env"

// Lazy load devtools only in development
const TanStackRouterDevtools = import.meta.env.DEV
	? lazy(() =>
			import("@tanstack/router-devtools").then(mod => ({
				default: mod.TanStackRouterDevtools,
			})),
		)
	: () => null

export const Route = createRootRoute({
	component: RootLayout,
	errorComponent: RootErrorComponent,
	notFoundComponent: NotFoundComponent,
})

/**
 * Auto-connect wallet in E2E mode.
 * Detects injected wallet and connects automatically.
 */
function E2EAutoConnect() {
	const { isConnected } = useAccount()
	const { connect, connectors } = useConnect()

	useEffect(() => {
		if (E2E_MODE && !isConnected && connectors.length > 0) {
			// Find the injected connector
			const injectedConnector = connectors.find(
				c => c.id === "injected" || c.name.toLowerCase().includes("metamask"),
			)
			if (injectedConnector) {
				// biome-ignore lint/suspicious/noConsole: E2E debug logging
				console.log("[E2E] Auto-connecting with:", injectedConnector.name)
				connect({ connector: injectedConnector })
			}
		}
	}, [isConnected, connectors, connect])

	return null
}

function RootLayout() {
	const { isConnected } = useAccount()
	const { setMode, isHotWalletMode, isConnectedViaSafe } = useExecutionMode()

	return (
		<div className="min-h-dvh bg-gray-950 text-white">
			{E2E_MODE && <E2EAutoConnect />}
			<header className="sticky top-0 z-[var(--z-header)] border-gray-800 border-b bg-gray-900/95 backdrop-blur">
				<div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
					<div className="flex items-center gap-6">
						<Link className="font-bold text-xl" preload="intent" to="/">
							<span className="text-gray-400">Breakout</span>
							<span className="ml-1 text-gray-400">Admin</span>
						</Link>

						{isConnected && (
							<nav className="hidden items-center gap-1 md:flex">
								<NavLink to="/">Command Center</NavLink>
								<NavLink to="/launch">Launch</NavLink>
								<NavLink to="/emergency">Emergency</NavLink>
							</nav>
						)}
					</div>

					<div className="flex items-center gap-3">
						{isConnected && (
							<div className="hidden items-center gap-2 lg:flex">
								<span className="text-gray-500 text-sm">Mode:</span>
								{isConnectedViaSafe ? (
									<span className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white">
										<span className="size-2 rounded-full bg-blue-300" />
										Safe Connected
									</span>
								) : (
									<>
										<button
											aria-pressed={isHotWalletMode}
											className={`rounded-lg px-3 py-1.5 text-sm transition ${
												isHotWalletMode
													? "bg-green-600 text-white"
													: "bg-gray-800 text-gray-400 hover:bg-gray-700"
											}`}
											onClick={() => setMode("hot-wallet")}
											type="button"
										>
											Hot Wallet
										</button>
										<button
											aria-pressed={!isHotWalletMode}
											className={`rounded-lg px-3 py-1.5 text-sm transition ${
												!isHotWalletMode
													? "bg-blue-600 text-white"
													: "bg-gray-800 text-gray-400 hover:bg-gray-700"
											}`}
											onClick={() => setMode("safe")}
											type="button"
										>
											Safe Batch
										</button>
									</>
								)}
							</div>
						)}
						<ConnectButton showBalance={false} />
					</div>
				</div>

				{/* Mobile nav */}
				{isConnected && (
					<nav className="flex gap-1 overflow-x-auto border-gray-800 border-t px-4 py-2 md:hidden">
						<NavLink to="/">Dashboard</NavLink>
						<NavLink to="/launch">Launch</NavLink>
						<NavLink to="/emergency">Emergency</NavLink>
					</nav>
				)}
			</header>

			<main className="mx-auto max-w-7xl px-4 py-6">
				<Outlet />
			</main>

			<Toaster
				position="bottom-center"
				theme="dark"
				toastOptions={{
					className: "!bg-gray-800 !border-gray-700 !text-white",
					descriptionClassName: "!text-gray-400",
				}}
				visibleToasts={3}
			/>

			<Suspense fallback={null}>
				<TanStackRouterDevtools position="bottom-right" />
			</Suspense>
		</div>
	)
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
	return (
		<Link
			className="whitespace-nowrap rounded-lg px-3 py-2 text-gray-400 text-sm transition hover:bg-gray-800 hover:text-white [&.active]:bg-gray-800 [&.active]:text-white"
			to={to}
		>
			{children}
		</Link>
	)
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
	const router = useRouter()

	useEffect(() => {
		if (error instanceof Error) {
			console.error("[AdminRootError]", error)
		}
	}, [error])

	return (
		<div className="min-h-dvh bg-gray-950 text-white">
			<div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
				<div className="mx-auto max-w-md text-center">
					<div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-red-900/50">
						<AlertCircle aria-hidden="true" className="size-8 text-red-400" />
					</div>
					<h1 className="mb-2 text-balance font-bold text-2xl text-white">Something went wrong</h1>
					<p className="mb-6 text-gray-400">
						{error instanceof Error ? error.message : "An unexpected error occurred"}
					</p>
					<div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
						<button
							className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-600 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
							onClick={() => {
								reset()
								router.invalidate()
							}}
							type="button"
						>
							<RefreshCw aria-hidden="true" className="size-4" />
							Try again
						</button>
						<Link
							className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-700 bg-gray-800 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
							to="/"
						>
							<Home aria-hidden="true" className="size-4" />
							Go home
						</Link>
					</div>
					{import.meta.env.DEV && error instanceof Error && error.stack && (
						<details className="mt-8 text-left">
							<summary className="cursor-pointer text-gray-500 text-sm hover:text-gray-400">
								Error details
							</summary>
							<pre className="mt-2 overflow-auto rounded-lg bg-gray-900 p-4 text-gray-300 text-xs">
								{error.stack}
							</pre>
						</details>
					)}
				</div>
			</div>
		</div>
	)
}

function NotFoundComponent() {
	return (
		<div className="min-h-dvh bg-gray-950 text-white">
			<div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
				<div className="mx-auto max-w-md text-center">
					<h1 className="mb-2 text-balance font-bold text-6xl text-white">404</h1>
					<h2 className="mb-4 text-balance font-semibold text-gray-400 text-xl">Page not found</h2>
					<p className="mb-8 text-gray-500">
						The page you're looking for doesn't exist or has been moved.
					</p>
					<Link
						className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-600 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
						to="/"
					>
						<Home aria-hidden="true" className="size-4" />
						Back to home
					</Link>
				</div>
			</div>
		</div>
	)
}
